import React from 'react'
import { useDwtStore } from './store'
import type {
    Blanks,
    ImageId,
    Page,
    PageFormState,
    PagesFormState,
    MatchedInputValues,
    PagesEntries,
    DwtDocuments,
    DwtDocument,
    Barcodes,
    ImageIds,
} from './types'

export const docTypeShortCodesMatch = (
    doc1: Pick<DwtDocument, 'docTypeShortCode'>,
    doc2: Pick<DwtDocument, 'docTypeShortCode'>,
): boolean => !!doc1.docTypeShortCode && doc1.docTypeShortCode === doc2.docTypeShortCode

export const assetsMatch = (doc1: Pick<DwtDocument, 'asset'>, doc2: Pick<DwtDocument, 'asset'>): boolean =>
    !!doc1.asset && doc1.asset === doc2.asset

export const documentInputValuesMatch = (doc1: MatchedInputValues, doc2: MatchedInputValues): boolean =>
    docTypeShortCodesMatch(doc1, doc2) && assetsMatch(doc1, doc2)

export const pageInputValues = (pg?: MatchedInputValues | null): MatchedInputValues =>
    pg
        ? {
              ...(pg.asset && { asset: pg.asset }),
              ...(pg.docTypeShortCode && { docTypeShortCode: pg.docTypeShortCode }),
          }
        : {}

export const pageContainsAnyInputValues = (pg: Page): boolean =>
    !!pg && Object.values(pageInputValues(pg)).some((v) => v)

export const pageContainsAllInputValues = (pg: Page): boolean =>
    !!pg && !!pg.asset && !!pg.docTypeShortCode && Object.values(pageInputValues(pg)).every((v) => v)

export type PagesToDocsProps = {
    imageIds: ImageIds
    formState?: PagesFormState
    blanks?: Blanks
    barcodes?: Barcodes
    autoCatalog?: boolean
    /** Docs will not contain blank pages */
    ignoreBlankPages: boolean
}

export const pagesToDocs = (props: PagesToDocsProps): DwtDocuments => {
    const { imageIds = [], formState = {}, blanks, barcodes, autoCatalog, ignoreBlankPages } = props || {}

    const imageIdsAsEntries = imageIds.map((id) => [id, {}])
    const formStateEntries = Object.entries(formState)
        .map(([formId, page]) => [
            // Convert formId to imageId (strip the 'id' prefix)
            formId.replace(/^id/, ''),
            page,
        ])
        .filter(([imageId]) => imageIds.includes(imageId as ImageId))

    const pagesEntries = Object.entries(
        Object.fromEntries([...imageIdsAsEntries, ...formStateEntries]),
    ) as unknown as PagesEntries

    if (!pagesEntries.length) {
        return []
    }

    return pagesEntries.reduce<DwtDocuments>((docs, pageEntry, idx) => {
        const imageId: ImageId = pageEntry[0] as unknown as ImageId

        if (Number.isNaN(imageId)) {
            return docs
        }

        const barcodeObject = barcodes?.get(imageId)
        const { docTypeShortCode, stockNumber, code: barcodeValue } = barcodeObject || {}
        const asset = typeof stockNumber === 'number' ? `${stockNumber}` : undefined
        const barcodeState = pageInputValues({ docTypeShortCode, asset })
        const pageState = pageEntry[1]
        const page: PageFormState = { ...barcodeState, ...pageState }

        if (!page) {
            return docs
        }

        const isCurrentPageBlank = blanks?.get(imageId)
        const priorPageEntry = pagesEntries[idx - 1]
        const priorImageId = priorPageEntry?.[0] as unknown as ImageId
        const isPriorPageBlank = blanks?.get(priorImageId)
        const priorDocIndex = docs.length - 1
        const priorDoc = docs[priorDocIndex] // TODO Node v16: docs.at(-1)
        const { imageIds: priorDocImageIds = [], ...priorDocMeta } = priorDoc || {}

        if (isCurrentPageBlank && ignoreBlankPages) {
            return docs
        }

        if (isCurrentPageBlank) {
            if (isPriorPageBlank) {
                return docs.slice(0, -1).concat({
                    imageIds: priorDocImageIds.concat(imageId),
                    blank: true,
                })
            }

            return docs.concat({ imageIds: [imageId], blank: true })
        }

        if (idx === 0) {
            // First overall page
            return [{ ...page, imageIds: [imageId] }]
        }

        if (docs.length === 0) {
            // First document (e.g. index=0 was blank)
            return [{ ...page, imageIds: [imageId] }]
        }

        const continueDocument =
            (!page.split &&
                (isPriorPageBlank ? ignoreBlankPages : true) &&
                pageContainsAnyInputValues(page) &&
                documentInputValuesMatch(page, priorDocMeta)) ||
            (autoCatalog &&
                !page.split &&
                (isPriorPageBlank ? ignoreBlankPages : true) &&
                !barcodeValue &&
                pageContainsAnyInputValues(priorDocMeta))

        if (continueDocument) {
            return docs.slice(0, -1).concat({
                ...priorDocMeta,
                imageIds: priorDocImageIds.concat(imageId),
            })
        }

        return docs.concat({ ...pageInputValues(page), imageIds: [imageId] })
    }, [])
}

export const useDocsCount = () => useDwtStore(React.useCallback((state) => state.computedDocs.length, []))
